import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

/**
 * ScrollAnimation Component
 *
 * A reusable component that triggers animations when elements come into view.
 * Uses Intersection Observer API for performance.
 *
 * Props:
 * - children: The content to animate
 * - animation: The animation type ('fade-up', 'fade-down', 'fade-left', 'fade-right', 'scale-in', 'rotate-in')
 * - delay: Delay in seconds before animation starts (default: 0)
 * - duration: Animation duration in seconds (default: 0.8)
 * - threshold: Percentage of element visible before triggering (default: 0.1)
 * - once: Whether animation should only trigger once (default: true)
 * - className: Additional CSS classes
 */

const ScrollAnimation = ({
  children,
  animation = 'fade-up',
  delay = 0,
  duration = 0.8,
  threshold = 0.1,
  once = true,
  className = '',
  ...props
}) => {
  const [ref, inView] = useInView({
    threshold,
    triggerOnce: once,
    rootMargin: '0px 0px -50px 0px',
  });

  const animationClasses = {
    'fade-up': 'animate-fade-up',
    'fade-down': 'animate-fade-down',
    'fade-left': 'animate-fade-left',
    'fade-right': 'animate-fade-right',
    'scale-in': 'animate-scale-in',
    'rotate-in': 'animate-rotate-in',
  };

  const animationClass = animationClasses[animation] || 'animate-fade-up';

  const style = {
    transitionDelay: `${delay}s`,
    transitionDuration: `${duration}s`,
  };

  return (
    <div
      ref={ref}
      className={`animate-on-scroll ${animationClass} ${inView ? 'visible' : ''} ${className}`}
      style={style}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * StaggeredAnimation Component
 *
 * Animates multiple children with staggered delays
 *
 * Props:
 * - children: Array of children to animate
 * - animation: The animation type
 * - staggerDelay: Delay between each child in seconds (default: 0.1)
 * - Other props same as ScrollAnimation
 */

export const StaggeredAnimation = ({
  children,
  animation = 'fade-up',
  staggerDelay = 0.1,
  ...props
}) => {
  const childrenArray = React.Children.toArray(children);

  return (
    <>
      {childrenArray.map((child, index) => (
        <ScrollAnimation
          key={index}
          animation={animation}
          delay={index * staggerDelay}
          {...props}
        >
          {child}
        </ScrollAnimation>
      ))}
    </>
  );
};

/**
 * CounterAnimation Component
 *
 * Animates numbers counting up when in view
 *
 * Props:
 * - end: The final number
 * - duration: Animation duration in seconds (default: 2)
 * - suffix: Text to append (e.g., '+', '%')
 * - prefix: Text to prepend (e.g., '$')
 */

export const CounterAnimation = ({
  end,
  duration = 2,
  suffix = '',
  prefix = '',
  className = '',
}) => {
  const [count, setCount] = React.useState(0);
  const [ref, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      let start = 0;
      const endValue = parseFloat(end);
      const increment = endValue / (duration * 60); // 60fps

      const timer = setInterval(() => {
        start += increment;
        if (start >= endValue) {
          setCount(endValue);
          clearInterval(timer);
        } else {
          setCount(start);
        }
      }, 1000 / 60);

      return () => clearInterval(timer);
    }
  }, [inView, end, duration]);

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return Math.floor(num).toLocaleString();
  };

  // Custom formatting for special suffixes like K+, M+, etc.
  let displayNumber = count;
  let displaySuffix = suffix;

  if (suffix === 'K+') {
    displayNumber = count;
    displaySuffix = 'K+';
  } else if (suffix === 'M+') {
    displayNumber = count;
    displaySuffix = 'M+';
  } else {
    displayNumber = formatNumber(count);
  }

  return (
    <span ref={ref} className={`counter-animate ${className}`}>
      {prefix}{displayNumber}{displaySuffix}
    </span>
  );
};

export default ScrollAnimation;
